'use client';

import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { useConsent } from '../../contexts/ConsentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePlayer } from '../../contexts/PlayerContext';
import type { Release, Track } from '../../types';
import { getBasePath } from '../../utils/basePath';
import MediaButtons from '../MediaButtons';

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(0, 255, 0, 0.4); }
  50% { box-shadow: 0 0 18px rgba(0, 255, 0, 0.8); }
`;

const bounceDown = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(6px); }
`;

const DetailContainer = styled.main<{ $bgColor: string }>`
  min-height: 100vh;
  background-color: ${({ $bgColor }) => $bgColor || '#111'};
  color: #fff;
  padding: 60px 20px 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const BackLink = styled(Link)`
  align-self: flex-start;
  max-width: 1100px;
  margin: 0 auto 30px;
  width: 100%;
  color: #fff;
  text-decoration: none;
  font-variant-caps: all-petite-caps;
  font-size: 1rem;
  letter-spacing: 1px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: color 0.2s;

  &:hover {
    color: #00ff00;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  align-items: start;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ArtworkWrapper = styled.div`
  position: sticky;
  top: 40px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.8);

  img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
`;

const InfoCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ReleaseTitle = styled.h1`
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-variant-caps: all-petite-caps;
  color: #fff;
  margin: 0;
  font-weight: 500;
`;

const ReleaseDate = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin: 0;
  font-variant-caps: all-petite-caps;
`;

const ReleaseDescription = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
`;

const PlayDirectButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 24px;
  background: #00ff00;
  color: #000;
  border: 1px solid #00ff00;
  font-variant-caps: all-petite-caps;
  font-weight: 700;
  letter-spacing: 1px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 0 15px rgba(0, 255, 0, 0.3);

  &:hover {
    background: #1aff1a;
    transform: scale(1.03);
    box-shadow: 0 0 25px rgba(0, 255, 0, 0.6);
  }
`;

const TracklistSection = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TracklistHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const TracklistTitle = styled.h2`
  font-size: 1.2rem;
  font-variant-caps: all-petite-caps;
  color: #fff;
  margin: 0;
`;

const TracklistSubtext = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  font-variant-caps: all-petite-caps;
`;

const TrackItem = styled.div<{ $active?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.12)' : 'rgba(255, 255, 255, 0.04)')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.4)' : 'rgba(255, 255, 255, 0.08)')};
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ $active }) =>
    $active &&
    css`
      animation: ${pulseGlow} 2s infinite ease-in-out;
    `}

  &:hover {
    background: rgba(0, 255, 0, 0.08);
    border-color: rgba(0, 255, 0, 0.3);
    transform: translateX(4px);
  }
`;

const TrackInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
`;

const PlayIconWrapper = styled.div<{ $active?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? '#00ff00' : 'rgba(255, 255, 255, 0.15)')};
  color: ${({ $active }) => ($active ? '#000' : '#fff')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  ${TrackItem}:hover & {
    background: #00ff00;
    color: #000;
  }
`;

const TrackDuration = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  font-variant-caps: all-petite-caps;
`;

const ScrollClosePrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 60px;
  padding: 16px 24px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  font-variant-caps: all-petite-caps;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    color: #00ff00;
    animation: ${bounceDown} 1.5s infinite ease-in-out;
  }

  &:hover {
    background: rgba(0, 255, 0, 0.08);
    border-color: rgba(0, 255, 0, 0.3);
    color: #fff;
  }
`;

export interface DetailProps {
  release: Release;
}

/**
 * Release Detail View acting as self-closing overlay with scroll-down close trigger.
 */
export const Detail: React.FC<DetailProps> = ({ release }) => {
  const navigate = useNavigate();
  const basePath = getBasePath();
  const {
    playTrack,
    togglePlay,
    skipToTrack,
    currentTrackUrl,
    currentTrackTitle,
    currentTrackIndex,
    isPlaying,
  } = usePlayer();
  const soundcloudUrl =
    (release.links?.find((l) => 'soundcloud' in l) as Record<string, string> | undefined)
      ?.soundcloud ||
    (
      release.links?.find(
        (l) => (l as unknown as { type?: string }).type === 'soundcloud'
      ) as unknown as { url?: string } | undefined
    )?.url;

  const isCurrentTrack = (track: Track, index: number) => {
    if (currentTrackUrl === soundcloudUrl) {
      if (typeof currentTrackIndex === 'number') {
        return currentTrackIndex === index;
      }
      return currentTrackTitle?.toLowerCase().includes(track.trackname.toLowerCase()) ?? false;
    }
    return false;
  };

  const { requestSoundCloudPermission, preferences } = useConsent();
  const { t } = useLanguage();
  const touchStartY = useRef(0);

  const handleClose = useCallback(() => {
    try {
      sessionStorage.setItem('joev_release_scroll_target', `release-${release.detail_link}`);
    } catch {
      // Ignore storage errors
    }
    navigate(`/#release-${release.detail_link}`);
  }, [release.detail_link, navigate]);

  // Listen for downward scrolling / gesture to trigger self-closing return
  useEffect(() => {
    let isTriggered = false;

    const onWheel = (e: WheelEvent) => {
      if (isTriggered) return;
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 60;
      if (e.deltaY > 30 && isAtBottom) {
        isTriggered = true;
        handleClose();
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (isTriggered) return;
      const currentY = e.touches[0].clientY;
      const diff = touchStartY.current - currentY;
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 60;
      if (diff > 50 && isAtBottom) {
        isTriggered = true;
        handleClose();
      }
    };

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [handleClose]);

  const handleTrackClick = (track: Track, index: number) => {
    if (!soundcloudUrl) return;

    if (!preferences.soundCloud) {
      const allowed = requestSoundCloudPermission(
        soundcloudUrl,
        `${track.trackname} — ${release.name}`,
        () => {
          playTrack(soundcloudUrl, `${track.trackname} — ${release.name}`, index);
        }
      );
      if (!allowed) return;
    }

    if (currentTrackUrl === soundcloudUrl && currentTrackIndex === index) {
      togglePlay();
    } else if (currentTrackUrl === soundcloudUrl) {
      skipToTrack(index, `${track.trackname} — ${release.name}`);
    } else {
      playTrack(soundcloudUrl, `${track.trackname} — ${release.name}`, index);
    }
  };

  return (
    <DetailContainer $bgColor={release.bgColor}>
      <BackLink to={`/#release-${release.detail_link}`}>{t('backToReleases')}</BackLink>

      <DetailGrid>
        <ArtworkWrapper>
          <img src={`${basePath}/assets/${release.cover}`} alt={release.name} />
        </ArtworkWrapper>

        <InfoCol>
          <ReleaseTitle>{release.name}</ReleaseTitle>
          <ReleaseDate>Released: {release.date}</ReleaseDate>
          <ReleaseDescription>
            {release.description || release.short_description}
          </ReleaseDescription>

          {soundcloudUrl && (
            <div>
              <PlayDirectButton
                onClick={() => {
                  if (currentTrackUrl === soundcloudUrl) {
                    togglePlay();
                  } else {
                    playTrack(
                      soundcloudUrl,
                      `${release.tracklist[0]?.trackname || release.name} — ${release.name}`,
                      0
                    );
                  }
                }}
                aria-label={`Play ${release.name} in SoundCloud player`}
              >
                {currentTrackUrl === soundcloudUrl && isPlaying ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16" />
                      <rect x="14" y="4" width="4" height="16" />
                    </svg>
                    Pause Album
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5,3 19,12 5,21" />
                    </svg>
                    ▶ Listen in Audio Player
                  </>
                )}
              </PlayDirectButton>
            </div>
          )}

          <TracklistSection>
            <TracklistHeader>
              <TracklistTitle>{t('tracklistTitle')}</TracklistTitle>
              <TracklistSubtext>Click any track to play</TracklistSubtext>
            </TracklistHeader>

            {release.tracklist.map((track, index) => {
              const active = isCurrentTrack(track, index);
              return (
                <TrackItem
                  key={track.tracknr + track.trackname}
                  $active={active}
                  onClick={() => handleTrackClick(track, index)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Play ${track.trackname}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleTrackClick(track, index);
                  }}
                >
                  <TrackInfo>
                    <PlayIconWrapper $active={active}>
                      {active && isPlaying ? (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="6" y="4" width="4" height="16" />
                          <rect x="14" y="4" width="4" height="16" />
                        </svg>
                      ) : (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          style={{ marginLeft: 1 }}
                        >
                          <polygon points="5,3 19,12 5,21" />
                        </svg>
                      )}
                    </PlayIconWrapper>
                    <span style={{ color: 'rgba(255,255,255,0.5)', minWidth: 20 }}>
                      {track.tracknr}
                    </span>
                    <span
                      style={{ color: active ? '#0f0' : '#fff', fontWeight: active ? 600 : 400 }}
                    >
                      {track.trackname}
                    </span>
                  </TrackInfo>
                  <TrackDuration>{track.duration}</TrackDuration>
                </TrackItem>
              );
            })}
          </TracklistSection>

          <div style={{ marginTop: 12 }}>
            <p
              style={{
                fontSize: '0.8rem',
                fontVariantCaps: 'all-petite-caps',
                color: 'rgba(255,255,255,0.6)',
                margin: '0 0 8px',
              }}
            >
              {t('streamingAndPurchase')}
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <MediaButtons bgColor={release.bgColor} links={release.links} isInViewport={true} />
            </div>
          </div>
        </InfoCol>
      </DetailGrid>

      <ScrollClosePrompt
        onClick={handleClose}
        role="button"
        tabIndex={0}
        aria-label="Scroll down to return to releases"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleClose();
        }}
      >
        <span>{t('detailScrollDownClose')}</span>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </ScrollClosePrompt>
    </DetailContainer>
  );
};

export default Detail;
